import { reactive } from 'vue'
import { api } from '@/services/api.js'
import { addToQueue, getQueuedNotes, removeFromQueue, isOnline, initOfflineDB, cacheTags, cacheNotes, getCachedNotes, addCachedNote, updateCachedNote, deleteCachedNote, addToDeleteQueue, getQueuedDeletes, removeFromDeleteQueue } from '@/services/offlineQueue.js'

// Clear all notes state (called on logout)
export function clearNotesState() {
  notesState.notes = []
  notesState.queuedNotes = []
  notesState.queuedDeletes = []
  notesState.error = null
  notesState.pagination = {
    page: 1,
    limit: 50,
    total: 0,
    hasMore: false
  }
}

export const notesState = reactive({
  notes: [],
  loading: false,
  error: null,
  queuedNotes: [],
  queuedDeletes: [],
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    hasMore: false
  }
})

// Initialize offline DB
initOfflineDB().catch(console.error)

// Helper to normalize note from API
function normalizeNote(note) {
  const tags = note.tags ? note.tags.split(',').map(t => t.trim()).filter(t => t) : []
  return {
    id: note.id.toString(),
    title: note.title || '',
    content: note.content || note.description || '',
    category: note.category || 'other',
    tags,
    createdAt: note.created_at,
    updatedAt: note.updated_at
  }
}

export async function loadNotes(page = 1, append = false) {
  notesState.loading = true
  
  // Load from cache ONLY on first load for instant display
  if (!append && page === 1) {
    try {
      const cachedNotes = await getCachedNotes()
      if (cachedNotes && cachedNotes.length > 0) {
        notesState.notes = cachedNotes
        notesState.loading = false // Show cached immediately
      }
    } catch (error) {
      console.error('Failed to load cached notes:', error)
    }
  }
  
  // Then fetch from server in background
  try {
    const response = await api.get(`/notes?page=${page}`)
    const data = response.data || response
    const rawNotes = data.notes || []
    const normalizedNotes = rawNotes.map(normalizeNote)
    
    if (append) {
      notesState.notes.push(...normalizedNotes)
    } else {
      // Only update if data changed
      const isSame = JSON.stringify(notesState.notes) === JSON.stringify(normalizedNotes)
      if (!isSame) {
        notesState.notes = normalizedNotes
      }
    }
    
    if (data.pagination) {
      notesState.pagination = data.pagination
    }
    
    // Cache in background without awaiting
    cacheNotes(notesState.notes).catch(console.error)
  } catch (error) {
    notesState.error = error.message
    console.error('Failed to load notes:', error)
  } finally {
    notesState.loading = false
  }
}

export async function loadMoreNotes() {
  if (!notesState.pagination.hasMore || notesState.loading) return
  const nextPage = notesState.pagination.page + 1
  await loadNotes(nextPage, true)
}

export async function createNote(noteData) {
  // Check if online
  if (!isOnline()) {
    try {
      await addToQueue(noteData)
      await loadQueuedNotes()
      return { queued: true, message: 'Note saved locally. Will sync when online.' }
    } catch (error) {
      if (error.message.includes('Cannot save more than')) {
        throw new Error('Cannot save more than 5 notes offline. Please connect to internet.')
      }
      throw error
    }
  }
  
  // Online - save to server
  try {
    const response = await api.post('/notes', noteData)
    const savedNote = response.data || response.note || response
    const normalized = normalizeNote(savedNote)
    
    // Add to beginning of list
    notesState.notes.unshift(normalized)
    
    // Cache in IndexedDB
    await addCachedNote(normalized)
    
    // Refresh tags after creating note
    try {
      const { loadAllTags } = await import('./tags.js')
      await loadAllTags()
    } catch (e) {
      console.error('Failed to refresh tags after create:', e)
    }
    
    return response
  } catch (error) {
    notesState.error = error.message
    throw error
  }
}

export async function updateNote(id, updates) {
  // Block edit if offline
  if (!isOnline()) {
    throw new Error('Cannot edit notes while offline. Please connect to internet.')
  }
  
  try {
    const response = await api.put(`/notes/${id}`, updates)
    const savedNote = response.data || response.note || response
    const normalized = normalizeNote(savedNote)
    
    // Update in list
    const index = notesState.notes.findIndex(n => n.id == id)
    if (index !== -1) {
      notesState.notes[index] = normalized
    }
    
    // Update in IndexedDB
    await updateCachedNote(id, normalized)
    
    // Refresh tags after updating note
    try {
      const { loadAllTags } = await import('./tags.js')
      await loadAllTags()
    } catch (e) {
      console.error('Failed to refresh tags after update:', e)
    }
    
    return response
  } catch (error) {
    notesState.error = error.message
    throw error
  }
}

export async function deleteNote(id) {
  // If offline, delete locally and queue for sync
  if (!isOnline()) {
    try {
      // Remove from local state
      notesState.notes = notesState.notes.filter(n => n.id != id)
      
      // Delete from IndexedDB
      await deleteCachedNote(id)
      
      // Add to delete queue
      await addToDeleteQueue(id)
      await loadQueuedDeletes()
      
      return { queued: true, message: 'Note deleted locally. Will sync when online.' }
    } catch (error) {
      throw error
    }
  }
  
  // Online - delete from server
  try {
    await api.delete(`/notes/${id}`)
    notesState.notes = notesState.notes.filter(n => n.id != id)
    
    // Delete from IndexedDB
    await deleteCachedNote(id)
    
    // Refresh tags after deleting note
    try {
      const { loadAllTags } = await import('./tags.js')
      await loadAllTags()
    } catch (e) {
      console.error('Failed to refresh tags after delete:', e)
    }
  } catch (error) {
    notesState.error = error.message
    throw error
  }
}

// Load queued notes from IndexedDB
export async function loadQueuedNotes() {
  try {
    const queued = await getQueuedNotes()
    notesState.queuedNotes = queued
  } catch (error) {
    console.error('Failed to load queued notes:', error)
  }
}

// Load queued deletes from IndexedDB
export async function loadQueuedDeletes() {
  try {
    const queued = await getQueuedDeletes()
    notesState.queuedDeletes = queued
  } catch (error) {
    console.error('Failed to load queued deletes:', error)
  }
}

// Sync queued notes to server
export async function syncQueuedNotes() {
  if (!isOnline()) return
  
  const results = { synced: 0, failed: 0, deleted: 0 }
  
  // Sync queued note creates
  for (const queuedNote of notesState.queuedNotes) {
    try {
      const response = await api.post('/notes', queuedNote)
      const savedNote = response.data || response.note || response
      const normalized = normalizeNote(savedNote)
      
      // Add to notes list
      notesState.notes.unshift(normalized)
      
      // Cache in IndexedDB
      await addCachedNote(normalized)
      
      // Remove from queue
      await removeFromQueue(queuedNote.id)
      results.synced++
    } catch (error) {
      console.error('Failed to sync note:', error)
      results.failed++
    }
  }
  
  // Sync queued deletes
  for (const queuedDelete of notesState.queuedDeletes) {
    try {
      await api.delete(`/notes/${queuedDelete.noteId}`)
      
      // Remove from delete queue
      await removeFromDeleteQueue(queuedDelete.noteId)
      results.deleted++
    } catch (error) {
      console.error('Failed to sync delete:', error)
      results.failed++
    }
  }
  
  // Reload queued items
  await loadQueuedNotes()
  await loadQueuedDeletes()
  
  return results
}

// Delete queued note
export async function deleteQueuedNote(id) {
  try {
    await removeFromQueue(id)
    await loadQueuedNotes()
  } catch (error) {
    console.error('Failed to delete queued note:', error)
    throw error
  }
}
